import type { ReactNode } from "react";
import type { Patch } from "@freightsimple/shared/types";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import { Grid } from "@freightsimple/ui-core/PageLayout/Grid";
import { Link } from "@freightsimple/ui-core/Typography/Link";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { ShowFormErrors } from "@freightsimple/ui-core/FormLayout/ShowFormErrors";
import { SplitRow } from "@freightsimple/ui-core/PageLayout/SplitRow";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";
import { getPrepositionForHandlingUnitType } from "@/Data/getPrepositionForHandlingUnitType";
import { getSingularFormForHandlingUnitType } from "@/Data/getSingularFormForHandlingUnitType";
import DangerousGoodsInput from "@/Shared/DangerousGoods/Inputs/DangerousGoodsInput";
import type { AddLineItemType } from "@/Shared/Types/lineItemTypes";
import type { LineItemErrors } from "../Validators/lineItemsErrorMessages";
import type { Units } from "@freightsimple/ui-core/Data/Units";
import type { LineItem } from "@freightsimple/generated-dashboard-openapi-client/models/LineItem";
import type { HandlingUnitType } from "@freightsimple/generated-dashboard-openapi-client/models/HandlingUnitType";
import { useEffect, useRef } from "react";
import scrollIntoView from "scroll-into-view-if-needed";
import { useLineItemsNmfcCoordination } from "../Hooks/useLineItemsNmfcCoordination";
import {
  lineItemsLabelForContentsSection,
  lineItemsLabelForDimensionsSection,
} from "../Helpers/lineItemsSectionLabels";
import { useLineItemsWarnings } from "../Hooks/useLineItemsWarnings";
import { LineItemsConvertUnitsButton } from "../Buttons/LineItemsConvertUnitsButton";
import DimensionsInput from "../Inputs/LineItemsDimensionsInput";
import LineItemsHandlingUnitsInput from "../Inputs/LineItemsHandlingUnitsInput";
import LineItemsIsStackableInput from "../Inputs/LineItemsIsStackableInput";
import { LineItemsFreightClassFields } from "../Internal/LineItemsFreightClassFields";
import LineItemsDimensionsQuestionBubble from "../Bubbles/LineItemsDimensionsQuestionBubble";
import LineItemsHandlingUnitsQuestionBubble from "../Bubbles/LineItemsHandlingUnitsQuestionBubble";
import LineItemsShipmentContentsQuestionBubble from "../Bubbles/LineItemsShipmentContentsQuestionBubble";
import LineItemsTemperatureHandlingInput from "../Inputs/LineItemsTemperatureHandlingInput";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import { touchesCanada } from "@/Helpers/touchesCanada";
import { CopyIcon } from "@phosphor-icons/react/Copy";
import { TrashIcon } from "@phosphor-icons/react/Trash";

interface LineItemContainerProps {
  lineItem: AddLineItemType;

  showRemoveLineItemButton: boolean;

  onChange: (value: Patch<LineItem>) => void;
  onAddLineItem: (() => void) | undefined;
  onRemoveLineItem: (() => void) | undefined;
  onDuplicateLineItem?: (() => void) | undefined;
  // Set once the shipment is at the line-item cap: Duplicate stays visible but
  // disabled, and this explains why on hover.
  duplicateDisabledMessage: string | undefined;

  lineItemErrorMessages: LineItemErrors;

  forceValidation: boolean;

  badge?: string | undefined;
  index: number;
  numberLineItems: number;

  hideBox?: boolean | undefined;

  shouldRenderFreightClass: boolean;

  setHasNmfcError: (_: boolean) => void;

  onRearrangeLengthWidth: () => void;

  units: Units;
  setUnits: (_: Units) => void;

  pickupLocation: AddLocationLocationType;
  deliveryLocation: AddLocationLocationType;

  protectFromFreezingOutOfSeason: boolean;
}

// A section heading inside the contents grid (e.g. "Handling Units"), with any
// inline help bubble passed as children.
//
// Each of these sits in a tight `gap={2}` Stack above its controls. The caption
// under a heading is the control's own label, and ui-core tucks a label onto its
// control (`-mb-1` in underlined presentation) — so with a roomier gap the
// caption drifts away from the heading and crowds the control instead, which
// reads badly above a tall row like the stepper + dropdown. Keeping every
// section on the same gap is also what keeps the two columns' controls on one
// line: they align by having identical structure, not by hand-matched offsets.
function SectionHeader_({ children }: { children: ReactNode }) {
  return (
    <T size="base" weight="semibold">
      {children}
    </T>
  );
}

function LineItemsContainer(props: LineItemContainerProps) {
  const { lineItem, lineItemErrorMessages, onChange } = props;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTimeout(() => {
      if (props.numberLineItems !== undefined && props.index !== undefined) {
        if (
          props.index > 1 &&
          props.index === props.numberLineItems &&
          ref.current
        ) {
          scrollIntoView(ref.current, {
            behavior: "smooth",
            block: "center",
            inline: "center",
          });
        }
      }
    });
  }, [props.numberLineItems]);

  const {
    nmfcLookupInProgress,
    nmfcErrorMessage,
    clearNmfcError,
    nmfcSubItems,
    nmfcDescription,
    onNmfcChange,
    onDimensionsChange,
  } = useLineItemsNmfcCoordination({
    lineItem,
    onChange,
    shouldRenderFreightClass: props.shouldRenderFreightClass,
    setHasNmfcError: props.setHasNmfcError,
  });

  function onRearrangeLengthWidth() {
    if (
      lineItemErrorMessages.height !== undefined ||
      lineItemErrorMessages.width !== undefined ||
      lineItemErrorMessages.length !== undefined
    ) {
      // Don't rearrange when there are errors
      return;
    }

    props.onRearrangeLengthWidth();
  }

  const multipleHandlingUnits = lineItem.numberHandlingUnits > 1;

  // Aliased so the hint below can hand it straight to `Link`, which wants a
  // handler and not `undefined`.
  const onAddLineItem = props.onAddLineItem;

  const showDifferentDimensionText =
    multipleHandlingUnits &&
    onAddLineItem !== undefined &&
    lineItemErrorMessages.handlingUnit === undefined;

  const warnings = useLineItemsWarnings(
    lineItem,
    lineItemErrorMessages,
    props.pickupLocation,
    props.deliveryLocation,
  );

  return (
    <div ref={ref}>
      <Panel
        collapseOnNarrow
        // Numbered once there's more than one card, so a customer working
        // through a long list can tell where they are against the cap. A
        // single card needs no number.
        cornerNumber={props.numberLineItems > 1 ? props.index : undefined}
      >
        <div id={`lineItemContainer${props.badge ?? props.index ?? "1"}`}>
          <Stack gap={6}>
            {/* Unequal columns, 3:4 — the left holds Handling Units and the
                "what's on this pallet" fields (the NMFC placeholder is the
                widest, ~338px), the right holds the dimensions row (L x W x H
                and weight inline, ~446px). Equal columns would need a ~1040
                panel to keep weight off its own line; 3:4 at the 920 section
                gives both sides their width. minColumnWidth is the left
                (smaller) column's floor: it's the binding one, needing ~338px
                for the NMFC placeholder, so below that the grid drops to one
                column rather than clipping it. */}
            <Grid columns={[3, 4]} gapX="lg" gapY="xl" minColumnWidth={338}>
              {/* Handling Units */}
              <Stack gap={2}>
                <SectionHeader_>
                  Handling Units{" "}
                  <LineItemsHandlingUnitsQuestionBubble
                    onAddLineItem={props.onAddLineItem}
                  />
                </SectionHeader_>
                <LineItemsHandlingUnitsInput
                  quantity={lineItem.numberHandlingUnits}
                  onQuantityChange={(newQuantity: number) =>
                    onChange({ numberHandlingUnits: newQuantity })
                  }
                  handlingUnitType={lineItem.handlingUnitType}
                  onHandlingUnitTypeChange={(newValue: HandlingUnitType) =>
                    onChange({ handlingUnitType: newValue })
                  }
                  forceValidation={true}
                  errorMessage={lineItemErrorMessages.handlingUnit}
                  hint={
                    showDifferentDimensionText ? (
                      <>
                        If each{" "}
                        {getSingularFormForHandlingUnitType(
                          lineItem.handlingUnitType,
                        ).toLowerCase()}{" "}
                        has different dims{" "}
                        <Link onClick={onAddLineItem}>add another row</Link>
                      </>
                    ) : undefined
                  }
                />
              </Stack>

              {/* Dimensions */}
              <Stack gap={2}>
                <SectionHeader_>
                  {lineItemsLabelForDimensionsSection(
                    lineItem.handlingUnitType,
                  )}{" "}
                  <LineItemsDimensionsQuestionBubble
                    handlingUnitType={lineItem.handlingUnitType}
                    onAddLineItem={props.onAddLineItem}
                  />
                </SectionHeader_>
                <DimensionsInput
                  onBlur={onRearrangeLengthWidth}
                  length={lineItem.length}
                  width={lineItem.width}
                  height={lineItem.height}
                  weight={lineItem.weightPerHandlingUnit}
                  onLengthChange={(value) =>
                    onDimensionsChange({ length: value })
                  }
                  onWidthChange={(value) =>
                    onDimensionsChange({ width: value })
                  }
                  onHeightChange={(value) =>
                    onDimensionsChange({ height: value })
                  }
                  onWeightChange={(value) =>
                    onDimensionsChange({ weightPerHandlingUnit: value })
                  }
                  lengthErrorMessage={lineItemErrorMessages.length}
                  widthErrorMessage={lineItemErrorMessages.width}
                  heightErrorMessage={lineItemErrorMessages.height}
                  weightErrorMessage={lineItemErrorMessages.weight}
                  forceValidation={props.forceValidation}
                  numberHandlingUnits={lineItem.numberHandlingUnits}
                  handlingUnitType={lineItem.handlingUnitType}
                  units={props.units}
                  maxSizeWarning={100}
                  minSizeWarning={2}
                  weightWarning={warnings.densityWarning}
                />
              </Stack>

              {/* What is on this pallet? */}
              <Stack gap={2}>
                <SectionHeader_>
                  {lineItemsLabelForContentsSection(
                    lineItem.handlingUnitType,
                    multipleHandlingUnits,
                  )}
                </SectionHeader_>
                <ShowFormErrors show={props.forceValidation}>
                  <FormItem
                    label={
                      <>
                        Description{" "}
                        <LineItemsShipmentContentsQuestionBubble
                          handlingUnitType={lineItem.handlingUnitType}
                          usePlural={multipleHandlingUnits}
                          preposition={
                            lineItem.handlingUnitType
                              ? getPrepositionForHandlingUnitType(
                                  lineItem.handlingUnitType,
                                )
                              : ""
                          }
                        />
                      </>
                    }
                    error={
                      warnings.showDescriptionAsWarning
                        ? undefined
                        : lineItemErrorMessages.description
                    }
                    warning={
                      warnings.showDescriptionAsWarning
                        ? warnings.descriptionWarning
                        : undefined
                    }
                  >
                    <TextInput
                      value={lineItem.description ?? ""}
                      onChange={(e) =>
                        onChange({ description: e.target.value })
                      }
                      placeholder="Enter Description"
                      data-testid="contents_description"
                    />
                  </FormItem>
                </ShowFormErrors>

                {props.shouldRenderFreightClass && (
                  <LineItemsFreightClassFields
                    lineItem={lineItem}
                    onNmfcChange={onNmfcChange}
                    nmfcLookupInProgress={nmfcLookupInProgress}
                    nmfcDescription={nmfcDescription}
                    nmfcErrorMessage={nmfcErrorMessage}
                    clearNmfcError={clearNmfcError}
                    nmfcSubItems={nmfcSubItems}
                    forceValidation={props.forceValidation}
                    freightClassErrorMessage={
                      lineItemErrorMessages.freightClass
                    }
                  />
                )}
              </Stack>

              {/* Special Handling */}
              <Stack gap={2}>
                <SectionHeader_>Special Handling</SectionHeader_>
                <LineItemsIsStackableInput
                  isStackable={lineItem.isStackable}
                  onChange={(value) => onChange({ isStackable: value })}
                  usePlural={multipleHandlingUnits}
                  handlingUnitType={lineItem.handlingUnitType}
                  errorMessage={lineItemErrorMessages.isStackable}
                  forceValidation={props.forceValidation}
                />
                <LineItemsTemperatureHandlingInput
                  temperatureHandling={lineItem.temperatureHandling}
                  onChange={(value) => onChange({ temperatureHandling: value })}
                  errorMessage={lineItemErrorMessages.temperatureHandling}
                  forceValidation={props.forceValidation}
                  protectFromFreezingOutOfSeason={
                    props.protectFromFreezingOutOfSeason
                  }
                />
                <DangerousGoodsInput
                  lineItem={lineItem}
                  onChange={function (update) {
                    onChange(update);
                  }}
                  errorMessage={lineItemErrorMessages.isDangerous}
                  forceValidation={props.forceValidation}
                  canadianTdgRules={touchesCanada(
                    props.pickupLocation.address.countryCode,
                    props.deliveryLocation.address.countryCode,
                  )}
                />
              </Stack>
            </Grid>

            <SplitRow
              left={
                <LineItemsConvertUnitsButton
                  units={props.units}
                  setUnits={props.setUnits}
                />
              }
              right={
                <Row gap={4}>
                  <Button
                    size="link"
                    icon={CopyIcon}
                    onClick={props.onDuplicateLineItem}
                    disabled={props.duplicateDisabledMessage !== undefined}
                    disabledMessage={props.duplicateDisabledMessage ?? ""}
                    data-testid="duplicate-line-item-button"
                  >
                    Duplicate this
                  </Button>
                  {props.showRemoveLineItemButton && (
                    <Button
                      size="link"
                      icon={TrashIcon}
                      onClick={props.onRemoveLineItem}
                      data-testid="remove-line-item-button"
                    >
                      Remove this
                    </Button>
                  )}
                </Row>
              }
            />
          </Stack>
        </div>
      </Panel>
    </div>
  );
}

export default LineItemsContainer;
